import { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SearchIcon from '@mui/icons-material/Search';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { listWps, setWpIdea } from '../api/dev';
import { usePermission } from '../hooks/usePermission';
import { WpTable } from '../components/dev/WpTable';
import { WpDetailDrawer } from '../components/dev/WpDetailDrawer';
import { UploadWpDialog } from '../components/dev/UploadWpDialog';
import { AddIdeaDialog } from '../components/dev/AddIdeaDialog';
import { useSnackbar } from '../context/SnackbarContext';
import type { WorkPackage, WpStatus } from '../types/dev';

type StatusFilter = 'all' | WpStatus;
type TypeFilter = 'wps' | 'ideas' | 'all';
type StatFilter = 'all' | WpStatus | 'bugs';

export function Development() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('wps');
  const [statFilter, setStatFilter] = useState<StatFilter>('all');
  const [drawerWpId, setDrawerWpId] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [ideaOpen, setIdeaOpen] = useState(false);
  const [ideasExpanded, setIdeasExpanded] = useState(true);
  const canWrite = usePermission('dev.write');

  const { data: wps = [] } = useQuery<WorkPackage[]>({
    queryKey: ['dev', 'wps'],
    queryFn: () => listWps(),
    refetchOnWindowFocus: true,
  });

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return wps.filter((wp) => {
      const matchesSearch =
        !q ||
        wp.wp_number.toLowerCase().includes(q) ||
        wp.title.toLowerCase().includes(q) ||
        (wp.description ?? '').toLowerCase().includes(q);
      const matchesStatus = statusFilter === 'all' || wp.status === statusFilter;
      const matchesType = typeFilter === 'all' || (typeFilter === 'wps' ? !wp.is_idea : wp.is_idea);
      const matchesStat = statFilter === 'all' || (statFilter === 'bugs' ? wp.bug_count > 0 : wp.status === statFilter);
      return matchesSearch && matchesStatus && matchesStat && matchesType;
    });
  }, [wps, search, statusFilter, typeFilter, statFilter]);

  const stats = useMemo(() => ({
    total: wps.length,
    wps: wps.filter((w) => !w.is_idea).length,
    ideas: wps.filter((w) => w.is_idea).length,
    done: wps.filter((w) => w.status === 'done').length,
    in_progress: wps.filter((w) => w.status === 'in_progress').length,
    blocked: wps.filter((w) => w.status === 'blocked').length,
    upcoming: wps.filter((w) => w.status === 'upcoming').length,
    bugs: wps.reduce((acc, w) => acc + w.bug_count, 0),
  }), [wps]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <CodeIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h5" fontWeight={700}>Development</Typography>
        </Stack>
        {canWrite && (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<LightbulbIcon />}
              onClick={() => setIdeaOpen(true)}
            >
              Add Idea
            </Button>
            <Button
              variant="contained"
              startIcon={<UploadFileIcon />}
              onClick={() => setUploadOpen(true)}
            >
              Upload Feature MD
            </Button>
          </Stack>
        )}
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        {([
          { key: 'all',         label: 'Total',       count: stats.total,       color: 'default'  },
          { key: 'done',        label: 'Done',        count: stats.done,        color: 'success'  },
          { key: 'in_progress', label: 'In Progress', count: stats.in_progress, color: 'info'     },
          { key: 'upcoming',    label: 'Upcoming',    count: stats.upcoming,    color: 'default'  },
          { key: 'blocked',     label: 'Blocked',     count: stats.blocked,     color: 'error'    },
          { key: 'bugs',        label: 'Bugs',        count: stats.bugs,        color: 'warning'  },
        ] as const).map(({ key, label, count, color }) => (
          <Chip
            key={key}
            label={`${label}: ${count}`}
            size="small"
            color={statFilter === key ? color : 'default'}
            variant={statFilter === key ? 'filled' : 'outlined'}
            onClick={() => setStatFilter(key)}
            sx={{ cursor: 'pointer', fontWeight: statFilter === key ? 700 : 400 }}
          />
        ))}
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search WP, title, description…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
          sx={{ minWidth: 280 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="upcoming">Upcoming</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="done">Done</MenuItem>
            <MenuItem value="blocked">Blocked</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={typeFilter}
            label="Type"
            onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
          >
            <MenuItem value="wps">WPs only</MenuItem>
            <MenuItem value="ideas">Ideas only</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {typeFilter !== 'ideas' && (
        <>
          <WpTable rows={filtered} onRowClick={(id) => setDrawerWpId(id)} />

          {stats.ideas > 0 && (
            <Box sx={{ mt: 3 }}>
              <Button
                size="small"
                startIcon={<LightbulbIcon />}
                onClick={() => setIdeasExpanded(!ideasExpanded)}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Ideas ({stats.ideas}) {ideasExpanded ? '▾' : '▸'}
              </Button>
              <Collapse in={ideasExpanded}>
                <IdeaTable rows={wps.filter((wp) => wp.is_idea)} onRowClick={(id) => setDrawerWpId(id)} />
              </Collapse>
            </Box>
          )}
        </>
      )}

      <WpDetailDrawer wpNumber={drawerWpId} onClose={() => setDrawerWpId(null)} />

      <UploadWpDialog open={uploadOpen} onClose={() => setUploadOpen(false)} />
      <AddIdeaDialog open={ideaOpen} onClose={() => setIdeaOpen(false)} />
    </Box>
  );
}

// ── IdeaTable — simplified table for ideas with Promote action ─────────────

function IdeaTable({ rows, onRowClick }: { rows: WorkPackage[]; onRowClick: (id: string) => void }) {
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();

  const handlePromote = async (wpId: number, wpNumber: string) => {
    try {
      await setWpIdea(wpId, false);
      void queryClient.invalidateQueries({ queryKey: ['dev', 'wps'] });
      showSnackbar(`${wpNumber} promoted to work package`, 'success');
    } catch {
      showSnackbar('Failed to promote idea', 'error');
    }
  };

  const columns: GridColDef[] = [
    { field: 'wp_number', headerName: 'WP', width: 90, renderCell: (p) => <Chip label={p.value} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontWeight: 700 }} /> },
    { field: 'title', headerName: 'Title', flex: 1, minWidth: 200, renderCell: (p) => <Typography variant="body2" fontWeight={600}>{p.value}</Typography> },
    { field: 'priority', headerName: 'Priority', width: 80, renderCell: (p) => <Typography variant="body2">{p.value}</Typography> },
    { field: 'status', headerName: 'Status', width: 130, renderCell: (p) => <Chip label={STATUS_LABELS[p.value as WpStatus]} size="small" sx={{ bgcolor: STATUS_COLORS[p.value as WpStatus], color: '#fff', fontSize: '0.7rem' }} /> },
    { field: 'generated_at', headerName: 'Created', width: 110, renderCell: (p) => <Typography variant="body2">{fmtDate(p.value)}</Typography> },
    {
      field: 'actions', headerName: '', width: 80, sortable: false,
      renderCell: (p) => (
        <Tooltip title="Promote to WP">
          <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); void handlePromote(p.row.id, p.row.wp_number); }}>
            <RocketLaunchIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <DataGrid
        rows={rows}
        columns={columns}
        density="compact"
        sx={{ cursor: 'pointer', '& .MuiDataGrid-row:hover': { bgcolor: 'action.hover' } }}
        disableRowSelectionOnClick={false}
        onRowClick={(params) => onRowClick(params.row.wp_number as string)}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } }, sorting: { sortModel: [{ field: 'wp_number', sort: 'desc' }] } }}
        pageSizeOptions={[10, 25]}
      />
    </Box>
  );
}

const STATUS_COLORS: Record<WpStatus, string> = { done: '#43a047', in_progress: '#1976d2', upcoming: '#9e9e9e', blocked: '#e53935' };
const STATUS_LABELS: Record<WpStatus, string> = { done: 'Done', in_progress: 'In Progress', upcoming: 'Upcoming', blocked: 'Blocked' };

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}
